import { FormEventHandler, useMemo, useState } from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { fetcher } from '../utils/fetcher'
import { Title } from 'react-head'
import { Button } from '@bm-js/atlascloud-ui'
import MinimalNav from '../components/MinimalNav'
import { colors, measurments } from '../utils/config'
import Input from '../components/Input'
import LinkButton from '../components/LinkButton'
import { ChangeEvent, UserBM } from '@bm-js/atlascloud-shared'
import { AuthActionType } from '../reducers/AuthState.reducer'
import { useAuthDispatch } from '../utils/hooks'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

const StyledLogin = styled.div`
  .content {
    padding-top: 4rem;
    padding-bottom: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 8rem);
    form {
      width: 45ch;
      background: white;
      padding: 3.2rem 4.5rem;
      border-radius: ${measurments.borderRadiusLarge}px;
      box-shadow: 0px 32px 64px -12px rgba(16, 24, 40, 0.2);
      h1 {
        font-weight: 700;
        font-size: 40px;
        line-height: 48px;
        letter-spacing: -2%;
        margin: 0;
        color: ${colors.primary400};
        margin-bottom: 2rem;
      }
    }
  }
  .error {
    text-align: center;
    color: ${colors.error400};
  }
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
`

const Login = () => {
  const initialUser = {
    email: '',
    password: '',
  }

  const { tr } = useTr()
  const dispatch = useAuthDispatch()

  const [formData, setFormData] = useState(initialUser)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e: ChangeEvent) => {
    setErrorMessage('')
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    setLoading(true)
    setErrorMessage('')
    const { err, data } = await fetcher<{ user: UserBM; token: string }>({
      path: 'customer-no-auth/login',
      method: 'POST',
      body: formData,
    })
    if (err || !data) {
      setErrorMessage(tr(TrKey.loginGenericError))
      setFormData({
        ...formData,
        password: '',
      })
      setLoading(false)
      return
    }
    dispatch({ type: AuthActionType.mergeState, data: { token: data.token } })
  }

  const title = useMemo(() => {
    return tr(TrKey.loginTitle) + ' | Atlascloud'
  }, [tr])

  return (
    <StyledLogin>
      <Title>{title}</Title>
      <MinimalNav />
      <div className='content'>
        <form onSubmit={handleSubmit}>
          <h1>{tr(TrKey.loginTitle)}</h1>
          <Input
            autoFocus
            required
            disabled={loading}
            label={tr(TrKey.loginEmailLabel)}
            type='email'
            value={formData.email}
            placeholder='exempel@mail.com'
            name='email'
            onChange={handleChange}
            autoComplete='email'
          />
          <Input
            required
            label={tr(TrKey.loginPasswordLabel)}
            disabled={loading}
            type='password'
            value={formData.password}
            name='password'
            onChange={handleChange}
            autoComplete='current-password'
          />
          <div className='jce ac flex'>
            <Link to='/forgot-password'>
              <LinkButton disabled={loading} className='mright1'>
                {tr(TrKey.loginForgotPassword)}
              </LinkButton>
            </Link>
            <Button type='submit' disabled={loading}>
              {loading ? tr(TrKey.loginLoggingIn) : tr(TrKey.loginTitle)}
            </Button>
          </div>
          {errorMessage && <p className='m0 mtop1 error'>{errorMessage}</p>}
        </form>
      </div>
    </StyledLogin>
  )
}

export default Login
